import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FiltersButton, Button, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { TemplateCard } from '../components/TemplateCard';
import { TemplateListItem } from '../components/TemplateListItem';
import { getStatusRank } from '../components/templateListShared';
import { useTemplates, useDeleteTemplate, useDuplicateTemplate } from '../hooks/useTemplates';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS, TemplateStatus } from '@troia-v3/types';
import type { TemplateQuery, TemplateResponse, TemplateStatusUpdatedEvent } from '@troia-v3/types';
import { useQueryClient } from '@tanstack/react-query';

type SortKey = 'name' | 'status' | 'variables' | 'createdAt';

const STATUS_OPTIONS: Array<{ value?: TemplateStatus; label: string }> = [
  { value: undefined, label: 'Todos' },
  { value: TemplateStatus.DRAFT, label: 'Rascunho' },
  { value: TemplateStatus.PENDING_APPROVAL, label: 'Pendente' },
  { value: TemplateStatus.APPROVED, label: 'Aprovado' },
  { value: TemplateStatus.REJECTED, label: 'Rejeitado' },
  { value: TemplateStatus.ARCHIVED, label: 'Arquivado' },
];

/**
 * Página de listagem de templates.
 * Toolbar 3 zonas + view toggle Lista/Cards + Filtros (popover claro fixo) +
 * filterbar de chips + seleção em massa. Grid vira cards no mobile.
 */
export const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();
  const confirm = useConfirm();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [query, setQuery] = useState<TemplateQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data, isLoading, error } = useTemplates(query);
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const { mutate: duplicateTemplate } = useDuplicateTemplate();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (paridade com o padrão de Usuários).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'status') cmp = getStatusRank(a.status) - getStatusRank(b.status);
      else if (sort.key === 'variables') cmp = (a.variables?.length ?? 0) - (b.variables?.length ?? 0);
      else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  // ── Socket.IO: atualizações de status de template (aprovação WhatsApp) ──
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleTemplateStatusUpdate = (eventData: TemplateStatusUpdatedEvent) => {
      const statusMessages: Record<string, { message: string; type: 'success' | 'error' | 'default' }> = {
        approved: { message: `Template "${eventData.name}" foi aprovado pelo WhatsApp! ✅`, type: 'success' },
        rejected: { message: `Template "${eventData.name}" foi rejeitado pelo WhatsApp. ${eventData.reason || ''}`, type: 'error' },
        pending_approval: { message: `Template "${eventData.name}" está aguardando aprovação do WhatsApp...`, type: 'default' },
      };

      const statusInfo = statusMessages[eventData.status] || {
        message: `Status do template "${eventData.name}" alterado para: ${eventData.status}`,
        type: 'default' as const,
      };

      if (statusInfo.type === 'success') {
        toast.success(statusInfo.message, { duration: 5000, position: 'top-right' });
      } else if (statusInfo.type === 'error') {
        toast.error(statusInfo.message, { duration: 6000, position: 'top-right' });
      } else {
        toast(statusInfo.message, { duration: 4000, position: 'top-right' });
      }

      queryClient.invalidateQueries({ queryKey: ['templates'] });
    };

    socket.on(SOCKET_EVENTS.TEMPLATE_STATUS_UPDATED, handleTemplateStatusUpdate);
    return () => {
      socket.off(SOCKET_EVENTS.TEMPLATE_STATUS_UPDATED, handleTemplateStatusUpdate);
    };
  }, [socket, isConnected, queryClient]);

  // ── Busca ──
  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  // ── Filtro de status (server-side) ──
  const setStatusFilter = (status?: TemplateStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const statusLabel = !Array.isArray(query.status) && query.status
    ? STATUS_OPTIONS.find((o) => o.value === query.status)?.label ?? null
    : null;
  const activeFilterCount = statusLabel ? 1 : 0;

  // ── Seleção em massa ──
  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((t) => selectedIds.has(t.id));
  const someOnPageSelected = items.some((t) => selectedIds.has(t.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((t) => next.delete(t.id));
      else items.forEach((t) => next.add(t.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // ── Ações CRUD ──
  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Tem certeza que deseja excluir este template?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteTemplate(id, {
      onError: (err) => toast.error(`Erro ao excluir template: ${err.message}`),
    });
  };

  const handleDuplicate = (id: string) => {
    duplicateTemplate(id, {
      onError: (err) => toast.error(`Erro ao duplicar template: ${err.message}`),
    });
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} template(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) =>
      deleteTemplate(id, { onError: (err) => toast.error(`Erro ao excluir template: ${err.message}`) }),
    );
    clearSelection();
  };

  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid="templates-select-all"
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey; className?: string }> = ({ label, k, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Prospecção
            </span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Templates</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar template…"
            data-testid="templates-search"
          />
        }
        right={
          <>
            {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="templates-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="templates-filters-popover"
                >
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
                  {STATUS_OPTIONS.map((opt) => {
                    const active = query.status === opt.value;
                    return (
                      <button
                        key={opt.label}
                        onClick={() => setStatusFilter(opt.value)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`templates-filter-status-${opt.value ?? 'all'}`}
                      >
                        {opt.label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="templates-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo template (único primário — F01-03: Button shared 38px, colapso @container) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/prospecting/templates/new')}
            data-testid="new-template"
          >
            Novo template
          </Button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status" data-testid="templates-chip-status-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="templates-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }} data-testid="templates-bulk-bar">
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="templates-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="templates-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }} data-testid="templates-error">
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar templates: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando templates..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhum template encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie seu primeiro template para começar a enviar mensagens personalizadas'}
              actionLabel="Criar primeiro template"
              onAction={() => navigate('/prospecting/templates/new')}
            />
          )}

          {/* Grid (Cards) — F34: cards sem checkbox, seleção só na view de lista */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sortedItems.map((template: TemplateResponse) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((template: TemplateResponse) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos')}
                      </th>
                      <SortHead label="Nome" k="name" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Provider</th>
                      <SortHead label="Status" k="status" />
                      <SortHead label="Variáveis" k="variables" />
                      <SortHead label="Criado em" k="createdAt" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((template: TemplateResponse) => (
                      <TemplateListItem
                        key={template.id}
                        template={template}
                        selected={selectedIds.has(template.id)}
                        onToggleSelect={toggleSelect}
                        onDelete={handleDelete}
                        onDuplicate={handleDuplicate}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação F48: totalItems/pageSize/itemLabel — "Mostrando X–Y de N templates" */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (data.total ?? 0) > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={data.total}
              pageSize={query.limit ?? 12}
              itemLabel="templates"
            />
          )}
        </div>
      </div>
    </div>
  );
};
